import { css } from '@emotion/react';
import { useState, useEffect, ChangeEvent, MouseEvent, forwardRef, KeyboardEvent } from 'react';
import { useRecoilState } from 'recoil';

import { useDetermineMobile } from '@myrealtrip/device-detect';
import { SEARCH_INPUT_HEIGHT } from '../../../constants/globalSearch';
import { LAYOUT, COLOR, Variation } from '../../../types/globalSearch';

import styled from '@emotion/styled';
import Icon from '../../../designSystem/components/Icon';
import { globalVariables } from '../../../designSystem/constants';

import { searchTerm } from '../recoil';
import qs from 'qs';

const SEARCH_ICON_GRAY = 'ic_search_20x20_gray_500';
const SEARCH_ICON_WHITE = 'ic_search_sm_white';

interface InputVariationType {
  searchIconName: string;
  border: string;
  borderRadius: number;
  hoverBgColor: string;
  hoverBorder: string;
  bgColor: string;
  fontColor: string;
  containerHeight: number;
  right: number;
  textAlign: string;
  imgSize: number;
}

function getSearchInputVariation({
  variations,
  isMobile,
}: {
  variations: Variation;
  isMobile: boolean;
}): InputVariationType {
  const { layout, color } = variations || {};
  let containerHeight = SEARCH_INPUT_HEIGHT.medium;
  let border = 'none';
  let borderRadius = 4;
  let right = 18;
  let textAlign = 'left';
  let imgSize = 20;
  let hoverBgColor = globalVariables.gray_80;
  let hoverBorder = 'none';

  if (layout === LAYOUT.pageContent) {
    if (!isMobile) {
      containerHeight = SEARCH_INPUT_HEIGHT.large;
      right = 102;
      imgSize = 24;
    }
    border = '1px solid rgba(255, 255, 255, 0.4)';
    borderRadius = 8;
    textAlign = 'center';
    hoverBgColor = 'rgba(255, 255, 255, 0.2)';
    hoverBorder = '1px solid rgba(255, 255, 255, 0.5)';
  }

  const defaultVariation = {
    searchIconName: SEARCH_ICON_GRAY,
    border,
    borderRadius,
    hoverBgColor,
    hoverBorder,
    bgColor: globalVariables.gray_60,
    fontColor: globalVariables.gray_700,
    containerHeight,
    right,
    textAlign,
    imgSize,
  };

  switch (color) {
    case COLOR.white:
      return defaultVariation;
    case COLOR.transparent:
      return {
        searchIconName: SEARCH_ICON_WHITE,
        border,
        borderRadius,
        hoverBgColor: 'rgba(245,246,247,.25)',
        hoverBorder,
        bgColor: 'rgba(245, 246, 257, 0.15)',
        fontColor: globalVariables.white,
        containerHeight,
        right,
        textAlign,
        imgSize,
      };
    case COLOR.blue:
      return {
        searchIconName: SEARCH_ICON_WHITE,
        border,
        borderRadius,
        hoverBgColor: globalVariables.blue_500,
        hoverBorder,
        bgColor: globalVariables.blue_500,
        fontColor: globalVariables.white,
        containerHeight,
        right,
        textAlign,
        imgSize,
      };
    default:
      return defaultVariation;
  }
}

interface Props {
  variations: Variation;
  placeholder: string;
  popupSide?: boolean;
  onPopupToggle?: (isOpen: boolean) => void;
  onDialogToggle?: (isOpen: boolean) => void;
  onPressKeyboard: (key: string) => void;
  onClearInput?: () => void;
  onClick?: () => void;
  onSearch?: () => void;
}

const SearchInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      variations,
      placeholder,
      popupSide,
      onPopupToggle,
      onDialogToggle,
      onPressKeyboard,
      onClearInput,
      onClick,
      onSearch,
    },
    ref,
  ) => {
    const [searchKeyword, setSearchKeyword] = useRecoilState(searchTerm);
    const isMobile = useDetermineMobile();
    const [isFocused, setIsFocused] = useState(false);
    const isEmpty = searchKeyword === '';

    const inputVariation = getSearchInputVariation({
      variations,
      isMobile,
    });

    const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchKeyword(value);
    };

    const handleKeywordClearClick = () => {
      onClearInput?.();
      setSearchKeyword('');
    };

    const handleInputFocus = () => {
      onPopupToggle?.(true);
      setIsFocused(true);
    };

    const handleInputBlur = () => {
      setIsFocused(false);
    };

    const handleContainerClick = () => {
      onDialogToggle?.(true);
    };

    const handleMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    };

    const handlePressKeyboard = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 229) {
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
      }
      onPressKeyboard(e.key);
    };

    useEffect(() => {
      if (isFocused && !popupSide) {
        onClick?.();
      }
    }, [isFocused, onClick, popupSide]);

    useEffect(() => {
      const currentUrl = window.location.href;
      const queryString = currentUrl.split('?')[1];
      const queryParams = qs.parse(queryString);

      if (queryParams.q) {
        const searchKeywordResult =
          new URLSearchParams(queryParams.q as string).get('query') || (queryParams.q as string);
        setSearchKeyword(searchKeywordResult);
      }
    }, []);

    return (
      <Container
        isFocused={isFocused}
        inputVariation={inputVariation}
        onClick={handleContainerClick}
      >
        <SearchIconContainer
          isFocused={!isEmpty || isFocused}
          inputVariation={inputVariation}
          onClick={onSearch}
        >
          <SearchIcon
            inputVariation={inputVariation}
            name={isFocused ? SEARCH_ICON_GRAY : inputVariation.searchIconName}
          />
        </SearchIconContainer>
        <KeywordInput
          id="globalSearchInput"
          ref={ref}
          value={searchKeyword}
          isEmpty={isEmpty}
          isFocused={isFocused}
          placeholder={placeholder}
          inputVariation={inputVariation}
          onChange={handleKeywordChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handlePressKeyboard}
          autoComplete="off"
        />
        {!isEmpty && isFocused && (
          <ClearButton
            type="button"
            onMouseDown={handleMouseDown}
            onClick={handleKeywordClearClick}
          >
            <Icon name="ic_clear_xs_filled_black30" />
          </ClearButton>
        )}
      </Container>
    );
  },
);

export default SearchInput;

const Container = styled.div<{
  isFocused: boolean;
  inputVariation: InputVariationType;
}>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  height: 48px;
  ${({ inputVariation }) => css`
    height: ${inputVariation.containerHeight}px;
    border: ${inputVariation.border};
    border-radius: ${inputVariation.borderRadius}px;
  `}
  ${({ isFocused }) =>
    isFocused &&
    css`
      box-shadow: 0px 0px 1px rgba(52, 58, 64, 0.2), 0px 1px 4px rgba(52, 58, 64, 0.15);
    `}
`;

const SearchIconContainer = styled.div<{
  isFocused: boolean;
  inputVariation: InputVariationType;
}>`
  position: absolute;
  right: ${({ inputVariation, isFocused }) => (isFocused ? 18 : inputVariation.right)}px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 48px;
  cursor: pointer;
`;

const SearchIcon = styled(Icon)<{ inputVariation: InputVariationType }>`
  ${({ inputVariation }) => css`
    width: ${inputVariation.imgSize}px;
    height: ${inputVariation.imgSize}px;
  `}
`;

const KeywordInput = styled.input<{
  isEmpty: boolean;
  isFocused: boolean;
  inputVariation: InputVariationType;
}>`
  width: 100%;
  height: 100%;
  ${({ isFocused, inputVariation }) =>
    css`
      background: ${isFocused ? globalVariables.white : inputVariation.bgColor};
    `}
  border: none;
  font-size: 15px;
  font-weight: 500;
  ${({ inputVariation, isFocused, isEmpty }) =>
    css`
      text-align: ${isFocused || !isEmpty ? 'left' : inputVariation.textAlign};
    `};
  padding: ${({ isFocused }) => (isFocused ? '0 72px 0 20px' : '0 54px 0 20px')};
  color: ${({ isFocused, inputVariation }) =>
    isFocused ? globalVariables.gray_700 : inputVariation.fontColor}; // gray-700
  transition: background-color 200ms cubic-bezier(0.4, 0, 0.2, 1);

  ::placeholder {
    color: ${({ inputVariation }) => inputVariation.fontColor} !important;
  }

  :hover {
    ${({ inputVariation, isFocused }) =>
      isFocused ||
      css`
        background-color: ${inputVariation.hoverBgColor};
      `};
    ${({ inputVariation, isFocused }) =>
      isFocused ||
      css`
        border: ${inputVariation.hoverBorder};
      `};
  }
`;

const ClearButton = styled.button`
  /* reset button style */
  position: absolute;
  right: 46px;
  margin: 0;
  padding: 0;
  appearance: none;
  border: none;
  outline: none;
  cursor: pointer;
  background: transparent;
  height: 100%;

  flex: 0 0 36px;
`;
